import { Draggable, Droppable } from '@hello-pangea/dnd';
import Fuse from 'fuse.js';

import { MarketDataCard } from '@src/components/MarketDataCard';

import type { MarketDataCardConfig } from '@talos/kyoko';
import { MixpanelEventSource, MixpanelSourceContext } from '@talos/kyoko';
import { MarketCardRfdDragProvider } from './MarketCardRfdDragContext';
import { DragWrapper, Filler } from './styles';

interface DroppableContainerProps {
  columnIndex: number;
  cardFilter: string;
  cards: MarketDataCardConfig[];
  disableDrag: boolean;
  lastColumnIndex: number;
  defaultSubAccountId: number | undefined;
  deleteCard: (card: MarketDataCardConfig) => void;
  saveCard: (card: MarketDataCardConfig) => void;
  hidePlaceholders: () => void;
}

export function DroppableContainer({
  columnIndex,
  cardFilter,
  cards,
  disableDrag,
  lastColumnIndex,
  deleteCard,
  saveCard,
  hidePlaceholders,
  defaultSubAccountId,
}: DroppableContainerProps) {
  const options = {
    includeMatches: true,
    includeScore: true,
    threshold: 0.2,
    keys: ['title', 'symbol', 'selectedMarketAccounts'],
  };

  let filteredCards: MarketDataCardConfig[];
  if (cardFilter && cardFilter !== '') {
    const fuse = new Fuse(cards, options);

    const matches = fuse.search(cardFilter);
    filteredCards = matches.map(match => match.item);
  } else {
    filteredCards = cards;
  }

  return (
    <Droppable droppableId={`${columnIndex}`}>
      {provided => (
        <div style={{ position: 'relative', height: '100%' }} ref={provided.innerRef}>
          {filteredCards.map((card, index) => {
            return (
              <Draggable isDragDisabled={disableDrag} key={card.id} draggableId={card.id} index={index}>
                {(provided, snapshot) => (
                  <DragWrapper ref={provided.innerRef} {...provided.draggableProps} {...provided.dragHandleProps}>
                    <MixpanelSourceContext.Provider value={MixpanelEventSource.MarketCard}>
                      <MarketCardRfdDragProvider dragHandleProps={provided.dragHandleProps}>
                        <MarketDataCard
                          config={card}
                          isDragged={snapshot.isDragging}
                          isDraggable={!disableDrag}
                          data-testid={`market-data-card-${card.symbol}`}
                          onDelete={deleteCard}
                          onSave={saveCard}
                          onHidePlaceholders={hidePlaceholders}
                          defaultSubAccountId={defaultSubAccountId}
                        />
                      </MarketCardRfdDragProvider>
                    </MixpanelSourceContext.Provider>
                  </DragWrapper>
                )}
              </Draggable>
            );
          })}
          {provided.placeholder}
          {cards.length === 0 && columnIndex <= lastColumnIndex && <Filler />}
        </div>
      )}
    </Droppable>
  );
}
